/**
 * Professional Tax calculator.
 * Jurisdiction = employee's branch.state (not a separate Work State field).
 */

function reviveMax(max) {
  if (
    max == null ||
    max === "" ||
    max === Infinity ||
    String(max).toLowerCase() === "infinity"
  ) {
    return Infinity;
  }
  return Number(max);
}

export function normalizeGender(gender) {
  const g = String(gender || "")
    .trim()
    .toLowerCase();
  if (g === "f" || g === "female" || g === "woman") return "female";
  if (g === "m" || g === "male" || g === "man") return "male";
  if (g) return "other";
  return "male";
}

export function normalizePtSlab(slab = {}) {
  return {
    min: Number(slab.min) || 0,
    max: reviveMax(slab.max),
    rate: Number(slab.rate) || 0,
  };
}

const DEFAULT_STATE_META = {
  applicable: true,
  frequency: "MONTHLY",
  grossBasis: "MONTHLY_GROSS",
  annualCap: null,
  needsVerification: false,
  lastVerified: null,
  sourceNotes: "",
  genderSlabs: null,
  specialRules: [],
  deductMonths: null,
  halfYearlyMode: "ON_SCHEDULE",
};

/** Convert legacy slab[] or rich object → canonical state config. */
export function normalizePtStateConfig(raw, stateName = "") {
  if (Array.isArray(raw)) {
    return {
      ...DEFAULT_STATE_META,
      slabs: raw.map(normalizePtSlab),
      sourceNotes: stateName
        ? `Legacy slabs for ${stateName}`
        : "Legacy slab list",
    };
  }
  if (!raw || typeof raw !== "object") {
    return {
      ...DEFAULT_STATE_META,
      slabs: [{ min: 0, max: Infinity, rate: 0 }],
    };
  }
  const genderSlabs = raw.genderSlabs && typeof raw.genderSlabs === "object"
    ? {
        male: (raw.genderSlabs.male || []).map(normalizePtSlab),
        female: (raw.genderSlabs.female || []).map(normalizePtSlab),
        other: (raw.genderSlabs.other || raw.genderSlabs.male || []).map(
          normalizePtSlab
        ),
      }
    : null;

  return {
    applicable: raw.applicable !== false,
    frequency:
      String(raw.frequency || "MONTHLY").toUpperCase() === "HALF_YEARLY"
        ? "HALF_YEARLY"
        : "MONTHLY",
    grossBasis: raw.grossBasis || "MONTHLY_GROSS",
    annualCap:
      raw.annualCap == null || raw.annualCap === ""
        ? null
        : Number(raw.annualCap),
    needsVerification: !!raw.needsVerification,
    lastVerified: raw.lastVerified || null,
    sourceNotes: raw.sourceNotes || "",
    slabs: (Array.isArray(raw.slabs) ? raw.slabs : []).map(normalizePtSlab),
    genderSlabs,
    specialRules: Array.isArray(raw.specialRules) ? raw.specialRules : [],
    deductMonths: Array.isArray(raw.deductMonths)
      ? raw.deductMonths.map((m) => Number(m)).filter((m) => m >= 1 && m <= 12)
      : null,
    halfYearlyMode:
      String(raw.halfYearlyMode || "ON_SCHEDULE").toUpperCase() ===
      "SPREAD_MONTHLY"
        ? "SPREAD_MONTHLY"
        : "ON_SCHEDULE",
  };
}

/** Normalize all states map (array or object values). */
export function normalizePtStatesMap(states = {}) {
  const out = {};
  Object.keys(states || {}).forEach((name) => {
    out[name] = normalizePtStateConfig(states[name], name);
  });
  return out;
}

export function getPtFromSlabs(gross, slabs) {
  if (!slabs || !slabs.length) return 0;
  const g = Number(gross) || 0;
  const slab = slabs.find((s) => {
    const max = reviveMax(s.max);
    return g >= Number(s.min) && g <= max;
  });
  return slab ? Number(slab.rate) || 0 : 0;
}

function pickSlabsForGender(stateConfig, gender) {
  const g = normalizeGender(gender);
  if (stateConfig.genderSlabs) {
    const byGender =
      stateConfig.genderSlabs[g] ||
      stateConfig.genderSlabs.male ||
      stateConfig.slabs;
    if (Array.isArray(byGender) && byGender.length) return byGender;
  }
  return stateConfig.slabs || [];
}

function applySpecialRules(amount, monthlyGross, month, gender, rules) {
  let next = amount;
  const g = normalizeGender(gender);
  const m = Number(month);
  for (const rule of rules || []) {
    if (!rule || String(rule.type).toUpperCase() !== "MONTH_OVERRIDE") continue;
    if (Number(rule.month) !== m) continue;
    if (rule.gender && normalizeGender(rule.gender) !== g) continue;
    const minGross = Number(rule.minGross ?? 0);
    if (monthlyGross < minGross) continue;
    if (rule.replaceAmount != null) {
      next = Number(rule.replaceAmount) || 0;
    } else if (Array.isArray(rule.slabs) && rule.slabs.length) {
      next = getPtFromSlabs(monthlyGross, rule.slabs.map(normalizePtSlab));
    } else if (rule.rate != null) {
      next = Number(rule.rate) || 0;
    }
  }
  return next;
}

/**
 * @param {object} opts
 * @param {object|array} opts.stateConfig - rich config or legacy slabs
 * @param {number} opts.monthlyGross
 * @param {string} [opts.gender]
 * @param {number} [opts.month] 1–12
 * @param {boolean} [opts.ptApplicable]
 * @param {number} [opts.ytdPtAmount] FY-to-date PT already deducted (for annual cap)
 * @param {string} [opts.stateName]
 * @returns {{ amount: number, frequency: string, reason?: string, capped?: boolean, needsVerification?: boolean }}
 */
export function calculatePT({
  stateConfig,
  monthlyGross,
  gender,
  month,
  ptApplicable = true,
  ytdPtAmount = 0,
  stateName = "",
} = {}) {
  const cfg = normalizePtStateConfig(stateConfig, stateName);
  if (!ptApplicable) {
    return { amount: 0, frequency: cfg.frequency, reason: "not_applicable" };
  }
  if (!cfg.applicable) {
    return {
      amount: 0,
      frequency: cfg.frequency,
      reason: "state_not_applicable",
      needsVerification: cfg.needsVerification,
    };
  }

  const g = Number(monthlyGross) || 0;
  const m = Number(month) || new Date().getMonth() + 1;
  let slabs = pickSlabsForGender(cfg, gender);
  let amount = getPtFromSlabs(g, slabs);
  amount = applySpecialRules(amount, g, m, gender, cfg.specialRules);

  if (cfg.frequency === "HALF_YEARLY") {
    const deductMonths = cfg.deductMonths?.length ? cfg.deductMonths : [9, 3];
    if (cfg.halfYearlyMode === "SPREAD_MONTHLY") {
      amount = Math.round((Number(amount) || 0) / 6);
    } else if (!deductMonths.includes(m)) {
      return {
        amount: 0,
        frequency: cfg.frequency,
        reason: "not_deduction_month",
        scheduledAmount: amount,
        deductMonths,
        needsVerification: cfg.needsVerification,
      };
    }
  }

  let capped = false;
  if (cfg.annualCap != null && Number.isFinite(Number(cfg.annualCap))) {
    const remaining = Math.max(
      0,
      Number(cfg.annualCap) - (Number(ytdPtAmount) || 0)
    );
    if (amount > remaining) {
      amount = remaining;
      capped = true;
    }
  }

  return {
    amount: Math.round(Number(amount) || 0),
    frequency: cfg.frequency,
    capped,
    needsVerification: cfg.needsVerification,
    stateName,
  };
}

/** Persist-friendly: Infinity → null inside slabs. */
export function serializePtStateConfig(cfg) {
  const n = normalizePtStateConfig(cfg);
  const mapSlabs = (list) =>
    (list || []).map((s) => ({
      min: Number(s.min) || 0,
      max: s.max == null || s.max === Infinity ? null : Number(s.max),
      rate: Number(s.rate) || 0,
    }));
  return {
    ...n,
    slabs: mapSlabs(n.slabs),
    genderSlabs: n.genderSlabs
      ? {
          male: mapSlabs(n.genderSlabs.male),
          female: mapSlabs(n.genderSlabs.female),
          other: mapSlabs(n.genderSlabs.other),
        }
      : null,
  };
}

export default calculatePT;
